import { useEffect, useRef, useState } from "react";
import { anzeigename } from "../../kern/namen";
import { personNach, type PersonKurz } from "../../kern/personen";
import { fehlertext } from "../../kern/fehler";
import { kurz } from "../../kern/zeit";
import { Feld } from "../../kern/ui/Feld";
import { Kalender } from "../../kern/ui/Kalender";
import { Knopf } from "../../kern/ui/Knopf";
import { Meldung } from "../../kern/ui/Leer";
import { useHinweis } from "../../kern/ui/Hinweise";
import { STATUS, type Aufgabe } from "./logik";
import { abschlussText, ohnePerson, ohnePersonZuerst, plusTage, sortierenImSprint, sprintName, vorschlagEnde, zeitraum, type Sprint } from "./sprintlogik";
import { useSitzungstermine, useSprintAktionen, useSprintVorschlag } from "./sprints";
import ui from "../../kern/ui/ui.module.css";
import s from "./sprint.module.css";

interface Props {
  offen: boolean;
  sprint: Sprint;
  /** Hauptaufgaben im Sprint */
  aufgaben: readonly Aufgabe[];
  personen: readonly PersonKurz[] | undefined;
  onSchliessen: () => void;
}

type Wohin = "weiter" | "backlog";

/** Dialog „Sprint abschließen“: Erledigtes geht ins Archiv, für Offenes wählt ihr nächster Sprint oder Backlog.
 *  Dabei entsteht der nächste Sprint. Vorschlag fürs Ende aus der Datenbank (aufgaben_sprint_vorschlag). */
export function SprintAbschluss({ offen, sprint, aufgaben, personen, onSchliessen }: Props) {
  const ref = useRef<HTMLDialogElement>(null);
  const kopf = useRef<HTMLHeadingElement>(null);
  const { zeigen } = useHinweis();
  const vorschlag = useSprintVorschlag(offen);
  const termine = useSitzungstermine();
  const { abschliessen } = useSprintAktionen();
  const [wohin, setWohin] = useState<Record<string, Wohin>>({});
  const [ende, setEnde] = useState<string | null>(null);
  const [ziel, setZiel] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (offen && !d.open) {
      d.showModal();
      kopf.current?.focus();
    }
    if (!offen && d.open) d.close();
  }, [offen]);

  const erledigt = aufgaben.filter((a) => a.status === "erledigt");
  const offene = ohnePersonZuerst(sortierenImSprint(aufgaben.filter((a) => a.status !== "erledigt")));
  const ohne = ohnePerson(offene);
  const endeWert = ende ?? vorschlag.data ?? vorschlagEnde(sprint.ende, termine.data ?? []);
  const beginn = plusTage(sprint.ende, 1);
  const naechster = sprintName(endeWert);
  const inBacklog = offene.filter((a) => wohin[a.id] === "backlog").map((a) => a.id);
  const weiter = offene.length - inBacklog.length;
  const endeOk = endeWert >= beginn;

  function schliessen() {
    setWohin({});
    setEnde(null);
    setZiel("");
    setFehler(null);
    onSchliessen();
  }

  function alle(w: Wohin) {
    setWohin(Object.fromEntries(offene.map((a) => [a.id, w])));
  }

  function absenden() {
    if (!endeOk) {
      setFehler(`Der nächste Sprint beginnt am ${kurz(beginn)}${beginn.slice(0, 4)}. Er endet frühestens an diesem Tag.`);
      return;
    }
    setFehler(null);
    abschliessen.mutate(
      { sprint: sprint.id, ende: endeWert, ziel: ziel.trim(), inBacklog },
      {
        onSuccess: () => {
          zeigen(`${sprint.name} ist im Archiv. ${naechster} läuft, ${weiter === 1 ? "1 Aufgabe" : `${weiter} Aufgaben`} übernommen.`);
          schliessen();
        },
        onError: (e) => setFehler(fehlertext(e)),
      },
    );
  }

  return (
    <dialog
      ref={ref}
      className={s.dialog}
      aria-labelledby="abschluss-titel"
      onCancel={(e) => {
        e.preventDefault();
        if (!abschliessen.isPending) schliessen();
      }}
    >
      <h2 id="abschluss-titel" ref={kopf} tabIndex={-1}>
        {sprint.name} abschließen
      </h2>
      <p>
        {erledigt.length === 1 ? "1 erledigte Aufgabe geht" : `${erledigt.length} erledigte Aufgaben gehen`} mit dem Sprint ins Archiv.{" "}
        {offene.length === 0 ? "Nichts ist mehr offen." : `${offene.length === 1 ? "1 ist" : `${offene.length} sind`} noch offen. Wohin damit?`}
      </p>
      {ohne ? (
        <p className={s.ohneHinweis}>
          {ohne === 1 ? "1 Aufgabe hat keine Person und steht" : `${ohne} Aufgaben haben keine Person und stehen`} oben. Was im Sprint ist, hat eine Person: Gib sie
          im nächsten Sprint jemandem oder leg sie in den Backlog.
        </p>
      ) : null}
      {offene.length > 0 ? (
        <>
          <div className={s.werkzeug}>
            <Knopf klein onClick={() => alle("weiter")}>
              Alle in {naechster}
            </Knopf>
            <Knopf klein onClick={() => alle("backlog")}>
              Alle in den Backlog
            </Knopf>
          </div>
          <ul className={s.liste} aria-label="Offene Aufgaben">
            {offene.map((a) => {
              const verantwortlich = a.personen.map((id) => personNach(personen, id)).filter((p) => !!p);
              return (
                <li key={a.id} className={s.abZeile}>
                  <div className={s.titelBlock}>
                    <strong>{a.titel}</strong>
                    <span className={s.meta}>
                      {verantwortlich.length ? verantwortlich.map((p) => p?.vorname || anzeigename(p)).join(", ") : "Ohne Person"} · {STATUS[a.status]}
                    </span>
                  </div>
                  <select
                    className={s.feld}
                    aria-label={`Wohin mit „${a.titel}“`}
                    value={wohin[a.id] ?? "weiter"}
                    onChange={(e) => setWohin({ ...wohin, [a.id]: e.target.value as Wohin })}
                  >
                    <option value="weiter">{naechster}</option>
                    <option value="backlog">Backlog</option>
                  </select>
                </li>
              );
            })}
          </ul>
        </>
      ) : null}
      <div className={[ui.innen, s.kasten].join(" ")}>
        <h3>Nächster Sprint</h3>
        <div className={s.zwei}>
          <div className={s.kopfFeld}>
            <span className={s.feldLabel}>Endet am</span>
            <Kalender label="Ende des nächsten Sprints" wert={endeWert} min={beginn} onWahl={(tag) => setEnde(tag)} />
            <span className={s.meta}>Vorschlag: die nächste Sitzung</span>
          </div>
          <div>
            <span className={s.meta}>Name und Zeitraum</span>
            <p style={{ margin: "6px 0 0", fontWeight: 600 }}>
              {naechster} · {endeOk ? zeitraum({ beginn, ende: endeWert }) : "Ende liegt vor dem Beginn"}
            </p>
          </div>
        </div>
        <Feld label="Sprintziel" value={ziel} maxLength={1000} placeholder={`Was soll am ${kurz(endeWert)} stehen?`} onChange={(e) => setZiel(e.target.value)} />
      </div>
      <p className={s.meta}>Danach holt ihr im Backlog weitere Aufgaben in {naechster.replace(/\.$/, "")}.</p>
      {fehler ? <Meldung fehler>{fehler}</Meldung> : null}
      <div className={s.knoepfe}>
        <Knopf onClick={schliessen} disabled={abschliessen.isPending}>
          Abbrechen
        </Knopf>
        <Knopf art="primaer" ohnePunkt onClick={absenden} disabled={abschliessen.isPending || !endeOk}>
          {abschliessen.isPending ? "Einen Moment …" : abschlussText(weiter, inBacklog.length)}
        </Knopf>
      </div>
    </dialog>
  );
}
